import axios, {
  AxiosError,
  AxiosHeaders,
  type AxiosInstance,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
} from 'axios';
import Bugsnag from '@bugsnag/js';
import { normalizeAxiosError } from './errors';
import { refreshSession } from '@/lib/session';
import { clearClientContext } from '@/lib/cleanup';

const API_URL = import.meta.env.VITE_API_URL as string;
const bugsnagApiKey = import.meta.env.VITE_BUGSNAG_API_KEY;
const isBugsnagEnabled = bugsnagApiKey !== undefined && bugsnagApiKey !== '';

type QueueItem = {
  resolve: (config: InternalAxiosRequestConfig) => void;
  reject: (reason?: Error) => void;
  config: InternalAxiosRequestConfig;
};

let isRefreshing = false;
let queue: QueueItem[] = [];

function flushQueue(error?: Error): void {
  const queueItemsToProcess = [...queue];
  queue = [];

  for (const { resolve, reject, config } of queueItemsToProcess) {
    if (error) {
      reject(error);
    } else {
      resolve(config);
    }
  }
}

export function createHttp(baseURL: string = API_URL, timeout = 20000): AxiosInstance {
  const instance = axios.create({
    baseURL,
    withCredentials: true,
    headers: { 'Content-Type': 'application/json' },
    timeout,
  });

  instance.interceptors.request.use(
    (requestConfig: InternalAxiosRequestConfig): InternalAxiosRequestConfig => {
      const requestHeaders = (requestConfig.headers ?? new AxiosHeaders()) as AxiosHeaders;
      requestHeaders.set('X-Requested-With', 'frontend');
      requestConfig.headers = requestHeaders;
      return requestConfig;
    },
  );

  instance.interceptors.response.use(
    (successfulResponse: AxiosResponse) => {
      return successfulResponse;
    },
    async (responseError: AxiosError): Promise<AxiosResponse<unknown> | never> => {
      const responseStatus = responseError.response?.status;
      const originalRequestConfig = responseError.config as InternalAxiosRequestConfig & {
        _retry?: boolean;
      };
      let shouldCleanupContext = false;

      if (isBugsnagEnabled) {
        const serverResponse = responseError.response;

        if (serverResponse !== undefined && serverResponse.status >= 500) {
          Bugsnag.notify(responseError, (event) => {
            event.severity = 'error';
            event.addMetadata('api', {
              requestUrl: responseError.config?.url,
              requestMethod: responseError.config?.method?.toUpperCase(),
              responseStatus: serverResponse.status,
              responseBody: serverResponse.data,
            });
          });
        }
      }

      if ((responseStatus === 401 || responseStatus === 419) && !originalRequestConfig?._retry) {
        if (isRefreshing) {
          return new Promise<AxiosResponse<unknown>>((resolve, reject) => {
            queue.push({
              resolve: (configAfterRefresh) => {
                void instance
                  .request(configAfterRefresh)
                  .then((retryResponse) => {
                    return resolve(retryResponse);
                  })
                  .catch((retryError: unknown) => {
                    return reject(
                      retryError instanceof Error
                        ? retryError
                        : new Error('Retried request failed'),
                    );
                  });
              },
              reject: (reason?: Error) => {
                reject(reason ?? new Error('Refresh queue rejected'));
              },
              config: originalRequestConfig,
            });
          });
        }

        originalRequestConfig._retry = true;
        isRefreshing = true;

        try {
          await refreshSession();
          isRefreshing = false;
          flushQueue();
          return instance.request(originalRequestConfig);
        } catch (refreshError: unknown) {
          isRefreshing = false;
          flushQueue(
            refreshError instanceof Error ? refreshError : new Error('Session refresh failed'),
          );
          shouldCleanupContext = true;
        }
      }

      if (
        shouldCleanupContext ||
        ((responseStatus === 401 || responseStatus === 419) &&
          Boolean(originalRequestConfig?._retry))
      ) {
        clearClientContext();
      }

      throw normalizeAxiosError(responseError);
    },
  );

  return instance;
}

export const http = createHttp();
